/**
 * Was in eingefuegtem HTML (insertAdjacentHTML) nie stehen darf — EINE Liste
 * fuer den Sanitizer des Servers, den jsdom-Pruefer und das Kunden-Bundle
 * (scanner#368). Vorher standen drei Kopien in den drei Paketen.
 *
 * Zwei Muster waren stumpfer als ihr Zweck und verweigerten harmlose
 * Fixes auf beiden Seiten gleich:
 *  - Ereignis-Handler: `/on\w+\s*=/` traf jedes „…on…=“, auch `aria-controls=`
 *    und `controls=` (beide sonst erlaubte Attribute). Jetzt nur ein Name, der
 *    mit `on` BEGINNT: davor steht kein Wortzeichen und kein Bindestrich. Was
 *    der Browser als Attributgrenze liest (`<span/onclick=`, `"x"onclick=`,
 *    Zeilenumbruch), bleibt erfasst.
 *  - `data:`: traf auch Fliesstext („Hinweis: data: …“). Gefaehrlich ist ein
 *    data:-URI als Attributwert — nur das wird verweigert.
 *
 * Klein und ohne Lookbehind gehalten: das Modul faehrt im Kunden-Bundle mit
 * (Gate 3), und aeltere Browser kennen `(?<!…)` nicht.
 */
export const DANGEROUS_HTML_PATTERNS: readonly RegExp[] = [
  /<script[\s>]/i,
  /javascript:/i,
  /(?:^|[^\w-])on\w+\s*=/i, // Ereignis-Handler als Attributname: onclick=, onerror=, …
  /<iframe[\s>]/i,
  /<object[\s>]/i,
  /<embed[\s>]/i,
  /<form[\s>]/i,
  /<input[\s>]/i,
  /<link[\s>]/i,
  /<meta[\s>]/i,
  /<style[\s>]/i,
  /=\s*["']?\s*data:/i, // data:-URI als Attributwert
  /vbscript:/i,
  /expression\s*\(/i, // CSS expression()
];

/** Das erste Muster, das dieses HTML verbietet, oder null. */
export function dangerousHtmlPattern(html: string): RegExp | null {
  for (const p of DANGEROUS_HTML_PATTERNS) if (p.test(html)) return p;
  return null;
}

/**
 * Positivliste fuer insertAdjacentHTML — EINE Liste fuer den Sanitizer des
 * Servers (fixer-server util/sanitize.ts) und das Kunden-Bundle (patch-applier,
 * Sicherheitspruefung 13.10.2026). Bis dahin pruefte der Server Attribute per
 * Regex, die zwei Formen nicht sah, die jeder Browser als Attribut liest:
 * `<div/x-data/x-init=…>` (Schraegstrich statt Leerzeichen) und
 * `<span title="a>" x-init=…>` (`>` im Wert beendete die Regex vorzeitig).
 * `x-init`, `@click`, `v-on:` … fuehren auf Seiten mit Alpine/Vue Code aus.
 * Jetzt werden Elemente und Attribute am GEPARSTEN Baum geprueft.
 */
export const INSERT_HTML_ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'span', 'div', 'label', 'p', 'strong', 'em', 'br', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'th', 'td',
]);

/** Attribute, die ein Patch setzen oder in eingefuegtem HTML tragen darf. */
export const ALLOWED_PATCH_ATTRIBUTES: ReadonlySet<string> = new Set([
  'id', 'class', 'role', 'tabindex', 'lang', 'dir', 'title', 'hidden',
  'for', // label for
  'alt', 'src', 'href', 'type', 'name', 'value', 'placeholder',
  'controls', 'muted', 'autoplay', // media attributes for F93/F23
  'autocomplete', 'required', 'scope', // form/table attributes
  'headers', // table cell → header ids (Paket FF 23.10.2026, F90: positional headers)
  'style', // inline styles (validated separately for dangerous CSS)
]);

/**
 * `content` an `<meta http-equiv="refresh">` — die EINZIGE Stelle, an der ein
 * Patch `content` setzen darf (F40/F58: verzoegerte Weiterleitung → sofort,
 * Nutzerentscheid 21.10.2026). Bewusst NICHT in ALLOWED_PATCH_ATTRIBUTES: die
 * Liste gilt fuer jedes Element und fuer eingefuegtes HTML. Der Server prueft
 * den Selektor (Subjekt `meta`) und den Wert, das Kunden-Bundle zusaetzlich
 * das echte Element (tagName META, http-equiv refresh).
 *
 * Erlaubter Wert: `<Zahl>` oder `<Zahl>;url=<URL>`. Die URL ist http(s) mit
 * `//` oder relativ — nie ein anderes Schema (javascript:, data:, vbscript:,
 * blob:, …), nie protokollrelativ (`//fremd.example`, auch `/\` und `\`,
 * die Browser wie `//` lesen), nie Leerraum, Anfuehrungszeichen, spitze
 * Klammern, Backtick oder Steuerzeichen. Fehlergrund oder null.
 */
export const META_REFRESH_CONTENT_ATTRIBUTE = 'content';

export function metaRefreshContentError(value: string): string | null {
  if (typeof value !== 'string') return 'meta refresh content must be a string';
  var m = /^(\d{1,5})(?:;url=(.+))?$/i.exec(value);
  if (!m) return 'meta refresh content must be "<seconds>" or "<seconds>;url=<URL>"';
  var url = m[2];
  if (url === undefined) return null;
  if (/[\s"'<>`\\\u0000-\u001f\u007f]/.test(url)) return 'meta refresh URL contains a disallowed character';
  if (/^\/\//.test(url)) return 'meta refresh URL must not be protocol-relative';
  var scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url);
  if (scheme) {
    var s = scheme[1].toLowerCase();
    if (s !== 'http' && s !== 'https') return 'meta refresh URL scheme "' + s + '" is not allowed';
    if (!/^https?:\/\/[^/?#]/i.test(url)) return 'meta refresh URL must be http(s)://host/…';
  }
  return null;
}

/** Ist das ECHTE Element ein `<meta http-equiv="refresh">`? (Kunden-Bundle) */
export function isMetaRefreshElement(el: { tagName: string; getAttribute(name: string): string | null }): boolean {
  return el.tagName.toUpperCase() === 'META'
    && (el.getAttribute('http-equiv') || '').trim().toLowerCase() === 'refresh';
}

/**
 * Seitentitel anlegen (Paket AX 21.10.2026, F25 / axe document-title): die
 * EINZIGE Form, in der ein Patch ein `<title>` erzeugen darf — eingefuegt in
 * `<head>`, reiner Text. `title` steht bewusst NICHT in INSERT_HTML_ALLOWED_TAGS
 * (die Liste gilt fuer jedes Ziel). Der Server prueft Selektor (`head`) und
 * Form, das Kunden-Bundle das echte Element (`document.head`), dass die Seite
 * noch KEINEN Titel hat, und legt das Element selbst an (textContent, kein
 * HTML-Parsing in die Seite). Text: 1–200 Zeichen ohne spitze Klammern; `&`
 * nur als Entitaet (der Generator kodiert ihn).
 */
export const PAGE_TITLE_INSERT_PATTERN = /^<title>([^<>]{1,200})<\/title>$/;

/** Ist `html` genau ein einzufuegender Seitentitel? */
export function isPageTitleInsert(html: string): boolean {
  return typeof html === 'string' && PAGE_TITLE_INSERT_PATTERN.test(html);
}

/**
 * Sprunglink einfuegen (Paket FF 23.10.2026, bypass-blocks ohne jeden
 * Umgehungsmechanismus): die EINZIGE Form, in der ein Patch ein `<a>` erzeugen
 * darf — `a` steht bewusst NICHT in INSERT_HTML_ALLOWED_TAGS. Ziel nur als
 * Fragment auf eine einfache id, feste Klasse, reiner Text (keine Entitaeten,
 * keine spitzen Klammern, keine Anfuehrungszeichen). Der Server prueft die
 * Form und die Position (vor bzw. am Anfang des gemeldeten Blocks), das
 * Kunden-Bundle zusaetzlich, dass das Ziel im Dokument existiert, und baut das
 * Element selbst (textContent, kein HTML-Parsing in die Seite) samt der
 * Regel „unsichtbar bis zum Fokus“.
 */
export const SKIP_LINK_CLASS = 'hb-skip-link';
export const SKIP_LINK_INSERT_PATTERN =
  /^<a href="#([A-Za-z][A-Za-z0-9_:.-]{0,63})" class="hb-skip-link">([^<>&"]{1,60})<\/a>$/;

/** Ist `html` genau ein einzufuegender Sprunglink? */
export function isSkipLinkInsert(html: string): boolean {
  return typeof html === 'string' && SKIP_LINK_INSERT_PATTERN.test(html);
}

/** Ziel-id und Text eines Sprunglinks, oder null. */
export function skipLinkInsertParts(html: string): { targetId: string; text: string } | null {
  var m = typeof html === 'string' ? SKIP_LINK_INSERT_PATTERN.exec(html) : null;
  return m ? { targetId: m[1], text: m[2] } : null;
}

/**
 * Die Stilregel des Sprunglinks: aus dem Bild geschoben, bei Fokus sichtbar
 * (das von bypass-blocks selbst anerkannte Muster „off-screen + :focus“).
 * Kein display:none — sonst waere der Link nie fokussierbar.
 */
export const SKIP_LINK_STYLE =
  '.hb-skip-link{position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden}'
  + '.hb-skip-link:focus{position:static;left:auto;width:auto;height:auto;overflow:visible}';

/**
 * Pause-Schalter fuer laufende Inhalte (Paket FX 27.10.2026, F16, SC 2.2.2):
 * die EINZIGE Form, in der ein Patch ein `<button>` erzeugen darf — `button`
 * steht bewusst NICHT in INSERT_HTML_ALLOWED_TAGS. Feste Klasse, Sprache aus
 * der Liste unten, Text genau die Pause-Beschriftung dieser Sprache. Der
 * Server prueft Form und Position (nur `beforebegin`: der Schalter steht VOR
 * dem bewegten Element, bewegt sich also nicht mit und wird von dessen
 * `overflow` nicht abgeschnitten). Das Kunden-Bundle baut den Schalter selbst
 * (textContent, kein HTML-Parsing in die Seite), prueft am lebenden Element,
 * dass es sich wirklich per CSS-Animation bzw. als `<marquee>` bewegt, und
 * haengt das Verhalten an: Klick/Enter/Leertaste haelt die Bewegung an und
 * setzt sie an derselben Stelle fort (G4).
 */
export const MOTION_PAUSE_CLASS = 'hb-motion-pause';
/** Attribut am bewegten Element, solange es angehalten ist (Stilregel unten). */
export const MOTION_PAUSED_ATTRIBUTE = 'data-hb-motion-paused';
/** Attribut am bewegten Element, sobald es einen HelpBob-Schalter hat (nie doppelt). */
export const MOTION_CONTROLLED_ATTRIBUTE = 'data-hb-motion-control';

/**
 * Beschriftung des Schalters je Seitensprache (dieselben acht Sprachen wie
 * der Sprunglink, Paket FF); jede andere Sprache bekommt Englisch. Ein
 * Schalter, dessen Beschriftung wechselt (APG-Karussell: „Stop/Start
 * automatic slide show“), kein aria-pressed dazu.
 */
export const MOTION_PAUSE_LABELS: { readonly [lang: string]: { readonly pause: string; readonly resume: string } } = {
  en: { pause: 'Pause animation', resume: 'Resume animation' },
  de: { pause: 'Bewegung anhalten', resume: 'Bewegung fortsetzen' },
  fr: { pause: 'Mettre l’animation en pause', resume: 'Reprendre l’animation' },
  es: { pause: 'Pausar la animación', resume: 'Reanudar la animación' },
  it: { pause: 'Metti in pausa l’animazione', resume: 'Riprendi l’animazione' },
  nl: { pause: 'Animatie pauzeren', resume: 'Animatie hervatten' },
  pt: { pause: 'Pausar a animação', resume: 'Retomar a animação' },
  pl: { pause: 'Wstrzymaj animację', resume: 'Wznów animację' },
};

export const MOTION_PAUSE_INSERT_PATTERN =
  /^<button type="button" class="hb-motion-pause" lang="([a-z]{2})">([^<>&"]{1,60})<\/button>$/;

/** Sprache und Beschriftungen eines Pause-Schalters, oder null (auch bei fremdem Text). */
export function motionPauseInsertParts(html: string): { lang: string; pause: string; resume: string } | null {
  var m = typeof html === 'string' ? MOTION_PAUSE_INSERT_PATTERN.exec(html) : null;
  if (!m || !Object.prototype.hasOwnProperty.call(MOTION_PAUSE_LABELS, m[1])) return null;
  var labels = MOTION_PAUSE_LABELS[m[1]];
  return m[2] === labels.pause ? { lang: m[1], pause: labels.pause, resume: labels.resume } : null;
}

/** Ist `html` genau ein einzufuegender Pause-Schalter? */
export function isMotionPauseInsert(html: string): boolean {
  return motionPauseInsertParts(html) !== null;
}

/** Der Einfuege-Text fuer eine Sprache (unbekannt → Englisch). */
export function motionPauseInsertHtml(lang: string | null | undefined): string {
  var key = typeof lang === 'string' ? lang.trim().toLowerCase().split(/[-_]/)[0] : '';
  if (!Object.prototype.hasOwnProperty.call(MOTION_PAUSE_LABELS, key)) key = 'en';
  return '<button type="button" class="' + MOTION_PAUSE_CLASS + '" lang="' + key + '">'
    + MOTION_PAUSE_LABELS[key].pause + '</button>';
}

/**
 * Vorfahren, in denen das Kunden-Bundle (und der jsdom-Pruefer) keinen
 * Pause-Schalter einfuegt: Bedienelemente (ein Button darin waere
 * verschachtelte Interaktion) und vor Hilfsmitteln verborgene bzw. inerte
 * Bereiche. Dieselbe Liste wie scanner utils/fix-facts.ts pauseControlSlot,
 * die schon beim Scan entscheidet, ob ein fester Fix entsteht.
 */
export const MOTION_PAUSE_BLOCKED_ANCESTORS =
  'a[href], button, label, summary, select, textarea, '
  + '[role="button"], [role="link"], [role="menuitem"], [role="option"], [role="tab"], [role="checkbox"], '
  + '[role="radio"], [role="switch"], [role="slider"], [contenteditable=""], [contenteditable="true"], '
  + '[aria-hidden="true"], [inert]';

/**
 * Die Stilregel: ein angehaltenes Element und alles darin steht still
 * (`animation-play-state` haelt eine CSS-Animation an der aktuellen Stelle an
 * und setzt sie dort fort). Der Schalter selbst behaelt das Aussehen des
 * Browsers (erkennbar, mit Fokusrahmen) und bekommt nur Mindestmass und
 * Abstand — 24×24 px fuer 2.5.8.
 */
export const MOTION_PAUSE_STYLE =
  '[data-hb-motion-paused],[data-hb-motion-paused] *{animation-play-state:paused!important}'
  + '.hb-motion-pause{min-width:24px;min-height:24px;margin:0.25em 0;cursor:pointer}';

/**
 * Schrift mit Zoom (Paket FX 27.10.2026, F94, SC 1.4.4): die EINE Form, die
 * der feste F94-Fix als `font-size` setzt — `clamp(<m>rem, <x>vw, <2m>rem)`.
 * Das Kunden-Bundle setzt sie nur, wenn `1rem` auf der Seite die vom Nutzer
 * eingestellte Grundgroesse ist (die Seite hat die Wurzel-Schriftgroesse
 * nicht veraendert) — sonst stimmten die rem-Grenzen nicht mit der
 * Rechnung des Generators ueberein.
 */
export const VIEWPORT_FONT_CLAMP_PATTERN =
  /^clamp\((\d+(?:\.\d{1,4})?)rem, (\d+(?:\.\d{1,3})?)vw, (\d+(?:\.\d{1,4})?)rem\)$/;

/** Ist `value` der font-size-Wert des festen F94-Fixes? */
export function isViewportFontClamp(value: string): boolean {
  return typeof value === 'string' && VIEWPORT_FONT_CLAMP_PATTERN.test(value);
}

/** on* (Ereignis-Handler) nie; aria-* und data-helpbob* immer; sonst die Liste. */
export function isAllowedAttribute(attr: string): boolean {
  const lower = attr.toLowerCase();
  if (lower.length > 2 && lower.slice(0, 2) === 'on') return false;
  if (lower.slice(0, 5) === 'aria-') return /^aria-[a-z]+$/.test(lower);
  if (lower.slice(0, 12) === 'data-helpbob') return /^data-helpbob(?:-[a-z0-9]+)*$/.test(lower);
  return ALLOWED_PATCH_ATTRIBUTES.has(lower);
}

/**
 * Prueft einen GEPARSTEN Baum gegen die Positivliste: nur Elemente und Text
 * (keine Kommentare o. ae.), nur erlaubte Tags, nur erlaubte Attribute.
 * Das erste Problem als Text, oder null. Ohne DOM-Typen gehalten, damit
 * Server (jsdom) und Bundle (DOMParser) dieselbe Funktion benutzen.
 */
export interface HtmlTreeNode {
  nodeType: number;
  nodeName: string;
  childNodes: ArrayLike<HtmlTreeNode>;
  attributes?: ArrayLike<{ name: string; value: string }>;
}

export function disallowedHtmlNode(root: HtmlTreeNode): string | null {
  const kids = root.childNodes;
  for (let i = 0; i < kids.length; i++) {
    const n = kids[i];
    if (n.nodeType === 3) continue; // Text
    if (n.nodeType !== 1) return 'HTML contains a non-element node: ' + n.nodeName;
    const tag = n.nodeName.toLowerCase();
    if (!INSERT_HTML_ALLOWED_TAGS.has(tag)) return 'HTML contains disallowed tag: <' + tag + '>';
    const attrs = n.attributes || [];
    for (let j = 0; j < attrs.length; j++) {
      if (!isAllowedAttribute(attrs[j].name)) return 'HTML contains disallowed attribute: ' + attrs[j].name;
    }
    const inner = disallowedHtmlNode(n);
    if (inner) return inner;
  }
  return null;
}
